import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { ToastContainer, toast } from "react-toastify";

const fdate = (d?: string | null) => (d ? new Date(d).toLocaleDateString("hr-HR") : "-");

const chip = (text: React.ReactNode, kind: "input" | "batch" | "customer" | "muted") => (
  <span className={`trace-chip is-${kind}`}>{text}</span>
);

const timelineStatusChip = (status?: string) => {
  switch (status) {
    case "confirmed":
      return chip("Potvrđen", "customer");
    case "draft":
      return chip("Nacrt", "muted");
    case "superseded":
      return chip("Zamijenjen", "muted");
    default:
      return chip(status || "-", "muted");
  }
};

interface Collection {
  key: string;
  label: string;
  icon: string;
  accent: string;
  load: () => Promise<any[]>;
  columns: { label: string; align?: "right" | "center" }[];
  search: (r: any) => string;
  rowKey: (r: any, i: number) => React.Key;
  cells: (r: any) => React.ReactNode[];
}

const toArray = async (p: Promise<any>): Promise<any[]> => {
  const res = await p;
  return Array.isArray(res) ? res : [];
};

const COLLECTIONS: Collection[] = [
  {
    key: "primke",
    label: "Primke",
    icon: "📦",
    accent: "#17a2b8",
    load: () => toArray(mesApi.getPrimke()),
    columns: [{ label: "Broj otpremnice" }, { label: "Datum" }, { label: "Dobavljač" }, { label: "Skladište" }, { label: "Stavki", align: "center" }, { label: "Karantena", align: "center" }],
    search: (r) => `${r.delivery_note_number ?? ""} ${r.supplier_name ?? ""} ${r.warehouse_name ?? ""}`,
    rowKey: (r) => r.delivery_receipt_id,
    cells: (r) => [
      <strong>{r.delivery_note_number || "-"}</strong>,
      fdate(r.received_date),
      r.supplier_name || "-",
      r.warehouse_name || "—",
      chip(r.lots?.length || 0, "batch"),
      r.quarantine_count > 0 ? chip(r.quarantine_count, "input") : "-",
    ],
  },
  {
    key: "narudzbe",
    label: "Narudžbe",
    icon: "🧾",
    accent: "#ffc107",
    load: () => toArray(mesApi.getOrderRequests()),
    columns: [{ label: "Broj" }, { label: "Datum" }, { label: "Rok isporuke" }, { label: "Status" }, { label: "Prioritet" }, { label: "Stavki", align: "center" }],
    search: (r) => `${r.RequestNumber ?? ""} ${r.Title ?? ""} ${r.StatusInfo?.Name ?? ""}`,
    rowKey: (r) => r.ID,
    cells: (r) => [
      <strong>{r.RequestNumber || `#${r.ID}`}</strong>,
      fdate(r.RequestDate),
      fdate(r.RequiredDeliveryDate),
      r.StatusInfo?.Name ? chip(r.StatusInfo.Name, "batch") : "-",
      r.Priority || "-",
      chip(r.LineCount ?? r.Lines?.length ?? 0, "muted"),
    ],
  },
  {
    key: "nalozi",
    label: "Radni nalozi",
    icon: "🏭",
    accent: "#53bde4",
    load: () => toArray(mesApi.getTimelines()),
    columns: [{ label: "Datum proizvodnje" }, { label: "Verzija" }, { label: "Status" }, { label: "Zamjesa", align: "center" }, { label: "Napomena" }],
    search: (r) => `${fdate(r.ProductionDate)} ${r.Status ?? ""} ${r.Notes ?? ""}`,
    rowKey: (r) => r.ID,
    cells: (r) => [
      <strong>{fdate(r.ProductionDate)}</strong>,
      `v${r.Version ?? 1}`,
      timelineStatusChip(r.Status),
      chip(r.TimelineBatch?.length ?? 0, "batch"),
      <span style={{ color: "#808080" }}>{r.Notes || "—"}</span>,
    ],
  },
  {
    key: "otpremnice",
    label: "Otpremnice",
    icon: "🚚",
    accent: "#28a745",
    load: () => toArray(mesApi.getDispatchNotes()),
    columns: [{ label: "Broj" }, { label: "Datum" }, { label: "Kupac" }, { label: "Stavki", align: "center" }],
    search: (r) => `${r.DispatchNumber ?? ""} ${r.Company_DispatchNoteToCompany?.Name ?? ""}`,
    rowKey: (r) => r.ID,
    cells: (r) => [
      <strong>{r.DispatchNumber || `#${r.ID}`}</strong>,
      fdate(r.DispatchDate),
      r.Company_DispatchNoteToCompany?.Name || "-",
      chip(r.DispatchNoteLine?.length ?? 0, "customer"),
    ],
  },
  {
    key: "recepti",
    label: "Recepti",
    icon: "📖",
    accent: "#9b8cff",
    load: () => toArray(mesApi.getRecipes()),
    columns: [{ label: "Šifra" }, { label: "Proizvod" }, { label: "Verzija" }, { label: "Sastojaka", align: "center" }, { label: "Status" }],
    search: (r) => `${r.RecipeCode ?? ""} ${r.Product_ProductToRecipe?.ProductName ?? r.SemiFinishedProduct?.Name ?? ""}`,
    rowKey: (r) => r.ID,
    cells: (r) => [
      <strong>{r.RecipeCode || `#${r.ID}`}</strong>,
      r.Product_ProductToRecipe?.ProductName || r.SemiFinishedProduct?.Name || "-",
      `v${r.RecipeVersion ?? 1}`,
      chip(r.RecipeIngredient_RecipeToRecipeIngredient_Recipe?.length ?? 0, "batch"),
      r.IsActive ? chip("Aktivan", "customer") : chip("Neaktivan", "muted"),
    ],
  },
  {
    key: "proizvodi",
    label: "Proizvodi",
    icon: "🍞",
    accent: "#e0884f",
    load: () => toArray(mesApi.getProducts()),
    columns: [{ label: "Šifra" }, { label: "Naziv" }, { label: "Kategorija" }, { label: "Masa zamjesa", align: "right" }, { label: "Status" }],
    search: (r) => `${r.ProductCode ?? ""} ${r.ProductName ?? ""} ${r.Category ?? ""}`,
    rowKey: (r) => r.ID,
    cells: (r) => [
      <strong>{r.ProductCode || `#${r.ID}`}</strong>,
      r.ProductName || "-",
      r.Category || "-",
      <span style={{ fontFamily: "monospace" }}>{r.DefaultBatchMassKg != null ? `${r.DefaultBatchMassKg} kg` : "-"}</span>,
      r.IsActive ? chip("Aktivan", "customer") : chip("Neaktivan", "muted"),
    ],
  },
  {
    key: "sirovine",
    label: "Sirovine",
    icon: "🌾",
    accent: "#c8a24b",
    load: () => toArray(mesApi.getIngredients({ raw_only: false })),
    columns: [{ label: "Naziv" }, { label: "Tip" }, { label: "Šifra" }, { label: "Status" }],
    search: (r) => `${r.IngredientName ?? ""} ${r.IngredientTypeName ?? ""} ${r.StandardCode ?? ""}`,
    rowKey: (r) => r.ID,
    cells: (r) => [
      <strong>{r.IngredientName || `#${r.ID}`}</strong>,
      r.IngredientTypeName || "-",
      r.StandardCode || r.RitamCode || "-",
      r.IsActive ? chip("Aktivna", "customer") : chip("Neaktivna", "muted"),
    ],
  },
  {
    key: "dobavljaci",
    label: "Dobavljači",
    icon: "🤝",
    accent: "#5aa9e6",
    load: () => toArray(mesApi.getSuppliers()),
    columns: [{ label: "Naziv" }, { label: "Kontakt" }, { label: "Email" }, { label: "Telefon" }],
    search: (r) => `${r.supplier_name ?? r.Name ?? ""} ${r.email ?? ""}`,
    rowKey: (r) => r.supplier_id ?? r.ID,
    cells: (r) => [
      <strong>{r.supplier_name || r.Name || "-"}</strong>,
      r.contact_person || "-",
      r.email || "-",
      r.phone || "-",
    ],
  },
  {
    key: "kupci",
    label: "Kupci",
    icon: "🏢",
    accent: "#4fd1c5",
    load: () => toArray(mesApi.getDispatchCustomers()),
    columns: [{ label: "Naziv" }, { label: "OIB" }, { label: "Adresa" }],
    search: (r) => `${r.Name ?? ""} ${r.OIB ?? ""}`,
    rowKey: (r) => r.ID,
    cells: (r) => [
      <strong>{r.Name || `#${r.ID}`}</strong>,
      r.OIB || "-",
      r.Address || "-",
    ],
  },
];

type Entry = { loading: boolean; rows: any[]; error?: string };

const RegistryOverview = () => {
  savePathToLocalStorage();

  const [store, setStore] = useState<Record<string, Entry>>(() =>
    Object.fromEntries(COLLECTIONS.map((c) => [c.key, { loading: true, rows: [] }]))
  );
  const [selected, setSelected] = useState<string>("primke");
  const [query, setQuery] = useState<string>("");

  useEffect(() => {
    COLLECTIONS.forEach((c) => {
      c.load()
        .then((rows) => setStore((s) => ({ ...s, [c.key]: { loading: false, rows } })))
        .catch((err) => {
          setStore((s) => ({ ...s, [c.key]: { loading: false, rows: [], error: err.message } }));
          toast.error(`${c.label}: ${err.message}`);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(() => COLLECTIONS.find((c) => c.key === selected)!, [selected]);
  const entry = store[selected];

  const filteredRows = useMemo(() => {
    if (!entry) return [];
    const q = query.trim().toLowerCase();
    if (!q) return entry.rows;
    return entry.rows.filter((r) => active.search(r).toLowerCase().includes(q));
  }, [entry, query, active]);

  return (
    <div className="mes-page">
      <Header title="MES - Pregled" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2500} />

          <div className="reg-head">
            <h2>Registar dokumenata</h2>
            <p>Pregled svih primki, narudžbi, radnih naloga, otpremnica, recepata i povezanih entiteta na jednom mjestu.</p>
          </div>

          <div className="reg-cards">
            {COLLECTIONS.map((c) => {
              const e = store[c.key];
              return (
                <button
                  key={c.key}
                  className={`reg-card ${selected === c.key ? "active" : ""}`}
                  style={{ ["--reg-accent" as any]: c.accent }}
                  onClick={() => {
                    setSelected(c.key);
                    setQuery("");
                  }}
                >
                  <div className="reg-card-top">
                    <span className="reg-card-icon">{c.icon}</span>
                    <span className={`reg-card-count ${e?.loading ? "loading" : ""}`}>
                      {e?.loading ? "…" : e?.error ? "!" : e?.rows.length ?? 0}
                    </span>
                  </div>
                  <div className="reg-card-label">{c.label}</div>
                </button>
              );
            })}
          </div>

          <div className="mes-section">
            <div className="reg-toolbar">
              <div className="reg-toolbar-title">
                <h3>{active.icon} {active.label}</h3>
                <span>{entry?.loading ? "učitavanje…" : `${filteredRows.length} zapisa`}</span>
              </div>
              <input
                className="reg-search"
                placeholder={`Pretraži ${active.label.toLowerCase()}…`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            {entry?.loading ? (
              <div className="trace-empty">Učitavanje…</div>
            ) : entry?.error ? (
              <div className="trace-empty">Greška: {entry.error}</div>
            ) : filteredRows.length === 0 ? (
              <div className="trace-empty">{query ? "Nema rezultata pretrage" : "Nema zapisa"}</div>
            ) : (
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    {active.columns.map((col, i) => (
                      <th key={i} style={col.align ? { textAlign: col.align } : undefined}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r, ri) => {
                    const cells = active.cells(r);
                    return (
                      <tr key={active.rowKey(r, ri)}>
                        {cells.map((cell, ci) => (
                          <td key={ci} style={active.columns[ci]?.align ? { textAlign: active.columns[ci].align } : undefined}>
                            {cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistryOverview;
